import {
  ErrorLogFilterOptionType,
  ErrorLogFiltersType,
  ErrorLogListItemType,
  ErrorLogSortDirectionType
} from 'types/error-log';
import { formatErrorLogType, formatErrorLogUser } from 'utils/error-log/format';

export const defaultErrorLogFilters: ErrorLogFiltersType = {
  userId: '',
  errorType: '',
  page: '',
  search: ''
};

export const userFilterValue = (log: ErrorLogListItemType): string => {
  if (log.userId !== null) {
    return String(log.userId);
  }
  return log.userEmail ?? log.userName ?? '';
};

const sortByLabel = (options: ErrorLogFilterOptionType[]) =>
  [...options].sort((a, b) => a.label.localeCompare(b.label));

const distinctOptions = (
  logs: ErrorLogListItemType[],
  toOption: (log: ErrorLogListItemType) => ErrorLogFilterOptionType
): ErrorLogFilterOptionType[] => {
  const byValue = new Map<string, ErrorLogFilterOptionType>();
  logs.forEach((log) => {
    const option = toOption(log);
    if (option.value && !byValue.has(option.value)) {
      byValue.set(option.value, option);
    }
  });
  return sortByLabel(Array.from(byValue.values()));
};

export const buildUserOptions = (logs: ErrorLogListItemType[]) =>
  distinctOptions(logs, (log) => ({
    label: formatErrorLogUser(log),
    value: userFilterValue(log)
  }));

export const buildErrorTypeOptions = (logs: ErrorLogListItemType[]) =>
  distinctOptions(logs, (log) => ({
    label: formatErrorLogType(log.errorType),
    value: log.errorType
  }));

export const buildPageOptions = (logs: ErrorLogListItemType[]) =>
  distinctOptions(logs, (log) => ({ label: log.page, value: log.page }));

export const hasActiveErrorLogFilters = (filters: ErrorLogFiltersType) =>
  Object.values(filters).some((value) => value.trim() !== '');

export const getLoggedColumnAriaSort = (
  sortable: boolean | undefined,
  direction: ErrorLogSortDirectionType
): 'ascending' | 'descending' | undefined => {
  if (!sortable) {
    return undefined;
  }
  if (direction === 'asc') {
    return 'ascending';
  }
  return 'descending';
};

export const sortErrorLogsByLogged = (
  logs: ErrorLogListItemType[],
  direction: ErrorLogSortDirectionType
): ErrorLogListItemType[] => {
  const factor = direction === 'asc' ? 1 : -1;
  return [...logs].sort((a, b) => {
    const byTime = a.createdAt.localeCompare(b.createdAt);
    if (byTime !== 0) {
      return byTime * factor;
    }
    return (a.id - b.id) * factor;
  });
};

export const errorLogMatchesFilters = (
  log: ErrorLogListItemType,
  filters: ErrorLogFiltersType
): boolean => {
  if (filters.userId && userFilterValue(log) !== filters.userId) {
    return false;
  }
  if (filters.errorType && log.errorType !== filters.errorType) {
    return false;
  }
  if (filters.page && log.page !== filters.page) {
    return false;
  }
  const search = filters.search.trim().toLowerCase();
  if (!search) {
    return true;
  }
  return [log.errorMessage, log.url, log.page, formatErrorLogUser(log)]
    .join(' ')
    .toLowerCase()
    .includes(search);
};
